import React, { useState, useMemo } from "react";
import { X, ShoppingBag, ChevronLeft, Tag, Check } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import INDIAN_STATES_CITIES from "@/data/indianLocations";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRODUCTS = [
  { id: "trial", name: "Trial Pack", price: 199, packets: "2 packets", weight: "160g" },
  { id: "value", name: "Value Pack", price: 499, packets: "5 packets", weight: "400g" },
  { id: "family", name: "Family Pack", price: 999, packets: "12 packets", weight: "960g" },
  { id: "large", name: "Large Pack", price: 237, packets: "1 pouch", weight: "300g" },
];

const RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY_ID || "rzp_test_placeholder";

const CheckoutDrawer = ({ isOpen, onClose, preselectedSku }) => {
  const [step, setStep] = useState(1); // 1=form, 2=summary, 3=cod-success
  const [cart, setCart] = useState({ trial: 0, value: 0, family: 0, large: 0 });
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    landmark: "",
    state: "",
    city: "",
    pincode: "",
    paymentMethod: "",
  });
  const [couponCode, setCouponCode] = useState("");
  const [couponApplied, setCouponApplied] = useState(false);
  const [couponError, setCouponError] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [promoApplied, setPromoApplied] = useState(false);
  const [promoError, setPromoError] = useState("");
  const [checkPincode, setCheckPincode] = useState("");
  const [pincodeMsg, setPincodeMsg] = useState(null);

  // Preselect SKU when opened
  React.useEffect(() => {
    if (preselectedSku && isOpen) {
      setCart(prev => ({ ...prev, [preselectedSku]: Math.max(prev[preselectedSku], 1) }));
    }
  }, [preselectedSku, isOpen]);

  // Reset on close
  React.useEffect(() => {
    if (!isOpen) {
      setStep(1);
      setCouponApplied(false);
      setCouponCode("");
      setCouponError("");
    }
  }, [isOpen]);

  const states = Object.keys(INDIAN_STATES_CITIES).sort();
  const cities = formData.state ? INDIAN_STATES_CITIES[formData.state] || [] : [];

  // Multi-SKU subtotal
  const subtotal = PRODUCTS.reduce((sum, p) => sum + p.price * (cart[p.id] || 0), 0);
  const cartItems = PRODUCTS.filter(p => cart[p.id] > 0);
  const hasItems = cartItems.length > 0;

  // Tiered founder coupons
  const COUPON_TIERS = [
    { code: "FOUNDER50", discount: 50, minSubtotal: 199, maxSubtotal: 999, label: "₹50 off" },
    { code: "FOUNDER100", discount: 100, minSubtotal: 1000, maxSubtotal: 2000, label: "₹100 off" },
    { code: "FOUNDER200", discount: 200, minSubtotal: 2001, maxSubtotal: 3000, label: "₹200 off" },
    { code: "FOUNDER300", discount: 300, minSubtotal: 3001, maxSubtotal: Infinity, label: "₹300 off" },
  ];

  const getEligibleTier = (code, sub) => COUPON_TIERS.find(t => t.code === code && sub >= t.minSubtotal && sub <= t.maxSubtotal);
  const bestEligibleTier = COUPON_TIERS.slice().reverse().find(t => subtotal >= t.minSubtotal && subtotal <= t.maxSubtotal);
  const appliedTier = couponApplied ? getEligibleTier(couponCode.trim().toUpperCase(), subtotal) : null;
  const discount = appliedTier ? appliedTier.discount : 0;
  const promoDiscount = promoApplied ? 20 : 0;
  const afterDiscount = subtotal - discount - promoDiscount;
  const codCharges = formData.paymentMethod === "cod" ? 100 : 0;
  const beforeGst = afterDiscount + codCharges;
  const gst = Math.round(beforeGst * 0.18 * 100) / 100;
  const total = Math.round((beforeGst + gst) * 100) / 100;

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "state") {
      setFormData(prev => ({ ...prev, state: value, city: "" }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleCartQty = (sku, delta) => {
    setCart(prev => ({ ...prev, [sku]: Math.max(0, Math.min(20, (prev[sku] || 0) + delta)) }));
    // Reset coupon if subtotal changes
    if (couponApplied) { setCouponApplied(false); setCouponCode(""); }
  };

  const handleApplyCoupon = (code) => {
    const tier = COUPON_TIERS.find(t => t.code === code);
    if (!tier) return;
    if (subtotal < tier.minSubtotal) {
      setCouponError(`Minimum subtotal of ₹${tier.minSubtotal} required for ${code}`);
      return;
    }
    if (subtotal > tier.maxSubtotal) {
      setCouponError(`${code} is valid for subtotals up to ₹${tier.maxSubtotal}`);
      return;
    }
    setCouponCode(code);
    setCouponApplied(true);
    setCouponError("");
  };

  const handleRemoveCoupon = () => {
    setCouponApplied(false);
    setCouponCode("");
    setCouponError("");
  };

  const isFormValid =
    hasItems &&
    formData.name && formData.email && formData.phone &&
    formData.address && formData.state && formData.city && formData.pincode &&
    formData.paymentMethod;

  const [orderId, setOrderId] = useState(null);
  const [payLoading, setPayLoading] = useState(false);

  const handleProceed = (e) => {
    e.preventDefault();
    if (isFormValid) setStep(2);
  };

  const handlePay = async () => {
    setPayLoading(true);

    try {
      // Step 1: Create order in backend (saves to MongoDB + Google Sheet with "Pending" status)
      // Build SKU string and description from cart
      const skuStr = cartItems.map(p => `${p.id}x${cart[p.id]}`).join(",");
      const totalQty = cartItems.reduce((sum, p) => sum + cart[p.id], 0);
      const descStr = cartItems.map(p => `${p.name} x${cart[p.id]}`).join(", ");
      const orderPayload = {
        sku: skuStr,
        quantity: totalQty,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        landmark: formData.landmark,
        city: formData.city,
        state: formData.state,
        pincode: formData.pincode,
        subtotal,
        discount: discount + promoDiscount,
        coupon_code: [couponApplied ? couponCode.trim().toUpperCase() : "", promoApplied ? "PUNEET20" : ""].filter(Boolean).join("+") || "",
        gst,
        total,
      };

      const res = await axios.post(`${API}/orders`, orderPayload);
      const createdOrderId = res.data.id;
      setOrderId(createdOrderId);

      // COD: mark as COD and show success
      if (formData.paymentMethod === "cod") {
        try {
          await axios.patch(`${API}/orders/${createdOrderId}/payment`, {
            payment_status: "cod_confirmed",
            payment_id: "COD",
          });
        } catch (e) {
          console.error("Failed to update COD status:", e);
        }
        if (typeof window.fbq === 'function') {
          window.fbq('track', 'Purchase', {
            value: total,
            currency: 'INR',
            content_name: descStr,
            content_ids: [skuStr],
            num_items: totalQty,
          });
        }
        setStep(3);
        setPayLoading(false);
        return;
      }

      // Online payment: Open Razorpay
      if (typeof window.Razorpay === "undefined") {
        toast.error("Payment gateway is loading. Please try again in a moment.");
        setPayLoading(false);
        return;
      }

      const options = {
        key: RAZORPAY_KEY,
        amount: Math.round(total * 100), // paise
        currency: "INR",
        name: "ETHIA",
        description: descStr,
        image: "https://customer-assets.emergentagent.com/job_2e8a6a93-30a9-4e91-a7a5-a9394acf49bd/artifacts/xwzvhvs5_Header%20Logo%20Ethia.png",
        prefill: {
          name: formData.name,
          email: formData.email,
          contact: formData.phone,
        },
        notes: {
          order_id: createdOrderId,
          sku: skuStr,
          quantity: String(totalQty),
        },
        theme: {
          color: "#0A1628",
        },
        handler: async function (response) {
          try {
            await axios.patch(`${API}/orders/${createdOrderId}/payment`, {
              payment_status: "completed",
              payment_id: response.razorpay_payment_id,
            });
          } catch (e) {
            console.error("Failed to update payment status:", e);
          }
          toast.success("Payment successful! Your order has been placed.");
          if (typeof window.fbq === 'function') {
            window.fbq('track', 'Purchase', {
              value: total,
              currency: 'INR',
              content_name: descStr,
              content_ids: [skuStr],
              num_items: totalQty,
            });
          }
          setPayLoading(false);
          setStep(3);
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", async function (response) {
        try {
          await axios.patch(`${API}/orders/${createdOrderId}/payment`, {
            payment_status: "failed",
            payment_id: response.error.metadata?.payment_id || "",
          });
        } catch (e) {
          console.error("Failed to update payment status:", e);
        }
        toast.error("Payment failed. Please try again.");
      });
      rzp.open();
    } catch (error) {
      console.error("Order creation failed:", error);
      toast.error("Failed to create order. Please try again.");
    } finally {
      setPayLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-[90] transition-opacity"
        onClick={onClose}
        data-testid="checkout-backdrop"
      />

      {/* Drawer */}
      <div
        className="fixed top-0 right-0 h-full w-full md:w-1/2 bg-white z-[100] shadow-2xl overflow-y-auto animate-[slideInRight_0.3s_ease-out]"
        style={{ cursor: "default" }}
        data-testid="checkout-drawer"
      >
        {step === 1 ? (
          /* ========== STEP 1: CHECKOUT FORM ========== */
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-4 flex items-center justify-between z-10">
              <div className="flex items-center gap-3">
                <ShoppingBag size={20} className="text-navy" />
                <h2 className="font-heading text-xl font-bold text-navy">Checkout</h2>
              </div>
              <button onClick={onClose} className="text-gray-400 hover:text-navy p-1" data-testid="close-checkout">
                <X size={24} />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleProceed} className="flex-1 px-5 py-6 space-y-5 overflow-y-auto pb-32">
              {/* Multi-Pack Cart */}
              <div>
                <label className="block text-navy text-sm font-semibold mb-2">Your Cart</label>
                <div className="space-y-2">
                  {PRODUCTS.map(p => (
                    <div key={p.id} className="flex items-center justify-between p-3 rounded-xl border-2 border-gray-200" data-testid={`sku-${p.id}`}>
                      <div>
                        <div className="font-semibold text-navy text-sm">{p.name}</div>
                        <div className="text-xs text-gray-500">{p.packets} · {p.weight} · <span className="font-bold text-navy">₹{p.price}</span></div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button type="button" onClick={() => handleCartQty(p.id, -1)} className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-navy font-bold text-sm hover:bg-gray-50" data-testid={`qty-minus-${p.id}`}>−</button>
                        <span className="font-bold text-navy text-sm w-6 text-center" data-testid={`qty-val-${p.id}`}>{cart[p.id] || 0}</span>
                        <button type="button" onClick={() => handleCartQty(p.id, 1)} className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-navy font-bold text-sm hover:bg-gray-50" data-testid={`qty-plus-${p.id}`}>+</button>
                      </div>
                    </div>
                  ))}
                </div>
                {hasItems && (
                  <div className="mt-3 text-right text-sm text-gray-500">Subtotal: <span className="font-bold text-navy">₹{subtotal}</span></div>
                )}
              </div>

              {/* Promo Code */}
              <div className="border-t border-gray-100 pt-4">
                <h3 className="text-navy font-semibold text-sm mb-2">Promo Code (if any)</h3>
                {!promoApplied ? (
                  <div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={promoCode}
                        onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromoError(""); }}
                        placeholder="Enter promo code"
                        className="flex-1 border-2 border-gray-200 focus:border-navy rounded-xl px-4 py-2.5 text-sm outline-none transition-colors"
                        data-testid="promo-input"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          if (promoCode.trim() === "PUNEET20") { setPromoApplied(true); setPromoError(""); }
                          else { setPromoApplied(false); setPromoError("Invalid promo code"); }
                        }}
                        className="px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors"
                        style={{ background: "#7B1F2E", color: "white" }}
                        data-testid="promo-apply-btn"
                      >Apply</button>
                    </div>
                    {promoError && <p className="text-red-500 text-xs mt-1.5">{promoError}</p>}
                  </div>
                ) : (
                  <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-xl px-4 py-2.5">
                    <span className="text-green-800 font-semibold text-sm">PUNEET20 applied — ₹20 off</span>
                    <button type="button" onClick={() => { setPromoApplied(false); setPromoCode(""); }} className="text-gray-400 hover:text-red-500 text-xs" data-testid="promo-remove">Remove</button>
                  </div>
                )}
              </div>

              {/* Divider */}
              <div className="border-t border-gray-100 pt-4">
                <h3 className="text-navy font-semibold text-sm mb-3">Personal Details</h3>
              </div>

              {/* Name */}
              <div>
                <label className="block text-navy text-xs font-medium mb-1.5">Full Name *</label>
                <input
                  type="text" name="name" value={formData.name} onChange={handleChange} required
                  className="w-full border-2 border-gray-200 focus:border-navy rounded-xl px-4 py-3 text-sm outline-none transition-colors"
                  placeholder="Enter your full name"
                  data-testid="input-name"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-navy text-xs font-medium mb-1.5">Email Address *</label>
                <input
                  type="email" name="email" value={formData.email} onChange={handleChange} required
                  className="w-full border-2 border-gray-200 focus:border-navy rounded-xl px-4 py-3 text-sm outline-none transition-colors"
                  placeholder="you@example.com"
                  data-testid="input-email"
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-navy text-xs font-medium mb-1.5">Phone Number *</label>
                <input
                  type="tel" name="phone" value={formData.phone} onChange={handleChange} required
                  className="w-full border-2 border-gray-200 focus:border-navy rounded-xl px-4 py-3 text-sm outline-none transition-colors"
                  placeholder="+91 XXXXX XXXXX"
                  data-testid="input-phone"
                />
              </div>

              {/* Divider */}
              <div className="border-t border-gray-100 pt-4">
                <h3 className="text-navy font-semibold text-sm mb-3">Check Delivery</h3>
              </div>

              {/* Pincode Checker */}
              <div>
                <label className="block text-navy text-xs font-medium mb-1.5">Check Delivery Pincode</label>
                <div className="flex gap-2">
                  <input
                    type="text" value={checkPincode} onChange={e => { setCheckPincode(e.target.value.replace(/\D/g,"")); setPincodeMsg(null); }}
                    maxLength={6} className="flex-1 border-2 border-gray-200 focus:border-navy rounded-xl px-4 py-3 text-sm outline-none transition-colors"
                    placeholder="Enter pincode" data-testid="check-pincode-input"
                  />
                  <button type="button" onClick={() => {
                    if (/^\d{6}$/.test(checkPincode)) setPincodeMsg({ ok: true, text: "Estimated delivery: 5 to 7 business days" });
                    else setPincodeMsg({ ok: false, text: "Please enter a valid 6-digit pincode." });
                  }} className="px-5 py-3 rounded-xl text-sm font-semibold text-white transition-colors" style={{ background: "#7B1F2E" }} data-testid="check-pincode-btn">Check</button>
                </div>
                {pincodeMsg && <p className={`text-xs mt-2 ${pincodeMsg.ok ? "text-green-600" : "text-red-500"}`}>{pincodeMsg.text}</p>}
              </div>

              {/* Divider */}
              <div className="border-t border-gray-100 pt-4">
                <h3 className="text-navy font-semibold text-sm mb-3">Shipping Address</h3>
              </div>

              {/* Primary Address */}
              <div>
                <label className="block text-navy text-xs font-medium mb-1.5">Primary Address *</label>
                <textarea
                  name="address" value={formData.address} onChange={handleChange} required rows="2"
                  className="w-full border-2 border-gray-200 focus:border-navy rounded-xl px-4 py-3 text-sm outline-none transition-colors resize-none"
                  placeholder="House/Flat no., Street, Area"
                  data-testid="input-address"
                />
              </div>

              {/* Landmark */}
              <div>
                <label className="block text-navy text-xs font-medium mb-1.5">Landmark</label>
                <input
                  type="text" name="landmark" value={formData.landmark} onChange={handleChange}
                  className="w-full border-2 border-gray-200 focus:border-navy rounded-xl px-4 py-3 text-sm outline-none transition-colors"
                  placeholder="Near..."
                  data-testid="input-landmark"
                />
              </div>

              {/* State & City */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-navy text-xs font-medium mb-1.5">State *</label>
                  <select
                    name="state" value={formData.state} onChange={handleChange} required
                    className="w-full border-2 border-gray-200 focus:border-navy rounded-xl px-3 py-3 text-sm outline-none transition-colors bg-white"
                    data-testid="select-state"
                  >
                    <option value="">Select State</option>
                    {states.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-navy text-xs font-medium mb-1.5">City *</label>
                  <select
                    name="city" value={formData.city} onChange={handleChange} required
                    disabled={!formData.state}
                    className="w-full border-2 border-gray-200 focus:border-navy rounded-xl px-3 py-3 text-sm outline-none transition-colors bg-white disabled:bg-gray-50 disabled:text-gray-400"
                    data-testid="select-city"
                  >
                    <option value="">{formData.state ? "Select City" : "Select state first"}</option>
                    {cities.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              {/* Pincode */}
              <div>
                <label className="block text-navy text-xs font-medium mb-1.5">Pincode *</label>
                <input
                  type="text" name="pincode" value={formData.pincode} onChange={handleChange} required
                  maxLength={6} pattern="[0-9]{6}"
                  className="w-full border-2 border-gray-200 focus:border-navy rounded-xl px-4 py-3 text-sm outline-none transition-colors"
                  placeholder="6-digit pincode"
                  data-testid="input-pincode"
                />
              </div>

              {/* Payment Method */}
              <div className="border-t border-gray-100 pt-4">
                <h3 className="text-navy font-semibold text-sm mb-3">Payment Method</h3>
              </div>
              <div className="space-y-2">
                {[
                  { id: "upi", label: "UPI", desc: "Pay by any UPI app" },
                  { id: "card", label: "Credit/Debit Card", desc: "Visa, Mastercard, Rupay & more" },
                  { id: "cod", label: "Cash on Delivery", desc: "Pay at your doorstep" },
                  { id: "netbanking", label: "NetBanking", desc: "Pay through your favourite bank" },
                ].map(pm => (
                  <label
                    key={pm.id}
                    className={`flex items-center justify-between p-3 rounded-xl border-2 cursor-pointer transition-all ${
                      formData.paymentMethod === pm.id
                        ? "border-[#7B1F2E] bg-[#7B1F2E]/5"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                    data-testid={`payment-${pm.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="radio"
                        name="paymentMethod"
                        value={pm.id}
                        checked={formData.paymentMethod === pm.id}
                        onChange={handleChange}
                        className="accent-[#7B1F2E] w-4 h-4"
                      />
                      <div>
                        <div className="font-semibold text-navy text-sm">{pm.label}</div>
                        <div className="text-xs text-gray-500">{pm.desc}</div>
                      </div>
                    </div>
                  </label>
                ))}
                {formData.paymentMethod === "cod" && (
                  <p className="text-[11px] italic pl-3 mt-1" style={{ color: "#7B1F2E" }}>Additional COD charges will be applied at the time of delivery.</p>
                )}
              </div>
            </form>

            {/* Proceed Button */}
            <div className="sticky bottom-0 bg-white border-t border-gray-100 px-5 py-4">
              <p className="text-center text-xs text-gray-400 mb-1">Secure checkout</p>
              <p className="text-center text-xs mb-3" style={{ color: "#7B1F2EB3" }}>Delivered in 5 to 10 business days.</p>
              <button
                onClick={handleProceed}
                disabled={!isFormValid}
                className="w-full text-white font-bold py-4 rounded-full text-base transition-colors disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90"
                style={{ background: "#7B1F2E" }}
                data-testid="proceed-btn"
              >
                Proceed to Order Summary &rarr;
              </button>
            </div>
          </div>
        ) : step === 2 ? (
          /* ========== STEP 2: ORDER SUMMARY ========== */
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-4 flex items-center justify-between z-10">
              <button
                onClick={() => setStep(1)}
                className="flex items-center gap-1 text-navy hover:text-navy-light text-sm font-medium"
                data-testid="back-to-form"
              >
                <ChevronLeft size={18} /> Back
              </button>
              <h2 className="font-heading text-xl font-bold text-navy">Order Summary</h2>
              <button onClick={onClose} className="text-gray-400 hover:text-navy p-1" data-testid="close-summary">
                <X size={24} />
              </button>
            </div>

            {/* Summary Content */}
            <div className="flex-1 px-5 py-6 space-y-5 overflow-y-auto pb-32">
              {/* Cart Items */}
              <div className="bg-gray-50 rounded-2xl p-4 space-y-3" data-testid="order-product-card">
                {cartItems.map(p => (
                  <div key={p.id} className="flex items-center justify-between">
                    <div>
                      <span className="font-semibold text-navy text-sm">{p.name}</span>
                      <span className="text-xs text-gray-400 ml-2">{p.packets} · {p.weight}</span>
                    </div>
                    <div className="text-right">
                      <span className="text-xs text-gray-500">₹{p.price} x {cart[p.id]}</span>
                      <span className="font-bold text-navy text-sm ml-3">₹{p.price * cart[p.id]}</span>
                    </div>
                  </div>
                ))}
                <div className="border-t border-gray-200 pt-2 flex justify-between">
                  <span className="text-sm text-gray-600">Subtotal</span>
                  <span className="font-bold text-navy">₹{subtotal}</span>
                </div>
              </div>

              {/* Shipping To */}
              <div className="bg-gray-50 rounded-2xl p-4">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Shipping To</h4>
                <p className="text-navy text-sm font-medium">{formData.name}</p>
                <p className="text-gray-600 text-xs mt-1">
                  {formData.address}{formData.landmark ? `, ${formData.landmark}` : ""}<br />
                  {formData.city}, {formData.state} - {formData.pincode}
                </p>
                <p className="text-gray-500 text-xs mt-1">{formData.phone} · {formData.email}</p>
              </div>

              {/* Payment Method — only selected */}
              <div className="bg-gray-50 rounded-2xl p-4" data-testid="summary-payment-method">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Payment Method</h4>
                <p className="text-navy text-sm font-medium">
                  {{ upi: "UPI", card: "Credit/Debit Card", cod: "Cash on Delivery", netbanking: "NetBanking" }[formData.paymentMethod]}
                </p>
                <p className="text-gray-500 text-xs mt-0.5">
                  {{ upi: "Pay by any UPI app", card: "Visa, Mastercard, Rupay & more", cod: "Pay at your doorstep", netbanking: "Pay through your favourite bank" }[formData.paymentMethod]}
                </p>
              </div>

              {/* Coupon Code — Click to Apply */}
              <div className="bg-gray-50 rounded-2xl p-4" data-testid="coupon-section">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Discount Coupons</h4>
                {!couponApplied ? (
                  <div className="space-y-2">
                    {couponError && <p className="text-red-500 text-xs mb-2">{couponError}</p>}
                    {COUPON_TIERS.map((tier) => {
                      const isUnlocked = subtotal >= tier.minSubtotal && (tier.maxSubtotal === Infinity || subtotal <= tier.maxSubtotal);
                      const isPast = tier.maxSubtotal !== Infinity && subtotal > tier.maxSubtotal;
                      const amountNeeded = tier.minSubtotal - subtotal;
                      return (
                        <div key={tier.code} className={`flex items-center justify-between rounded-xl px-3 py-2.5 border transition-all ${isUnlocked ? "border-green-300 bg-green-50" : isPast ? "border-gray-100 bg-gray-50 opacity-40" : "border-gray-200 bg-white"}`} data-testid={`tier-${tier.code}`}>
                          <div>
                            <span className={`font-bold text-xs ${isUnlocked ? "text-green-700" : "text-gray-500"}`}>{tier.code}</span>
                            <span className={`ml-2 text-xs ${isUnlocked ? "text-green-600" : "text-gray-400"}`}>{tier.label}</span>
                          </div>
                          {isUnlocked ? (
                            <button type="button" onClick={() => handleApplyCoupon(tier.code)} className="text-xs font-bold px-3 py-1 rounded-full bg-green-600 text-white hover:bg-green-700 transition-colors" data-testid={`apply-${tier.code}`}>Apply</button>
                          ) : isPast ? (
                            <span className="text-gray-300 text-xs">—</span>
                          ) : (
                            <span className="text-gray-400 text-xs">Add ₹{amountNeeded} more</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-xl px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Check size={16} className="text-green-600" />
                      <span className="text-green-800 font-semibold text-sm">{couponCode} applied</span>
                      <span className="text-green-600 text-xs">(-₹{discount.toFixed(2)})</span>
                    </div>
                    <button type="button" onClick={handleRemoveCoupon} className="text-gray-400 hover:text-red-500 text-xs" data-testid="remove-coupon-btn">Remove</button>
                  </div>
                )}
              </div>

              {/* Price Breakdown */}
              <div className="space-y-3 pt-2" data-testid="price-breakdown">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="text-navy font-medium">₹{subtotal.toFixed(2)}</span>
                </div>
                {couponApplied && discount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-green-600">Discount ({couponCode.trim().toUpperCase()})</span>
                    <span className="text-green-600 font-medium">-₹{discount.toFixed(2)}</span>
                  </div>
                )}
                {promoApplied && (
                  <div className="flex justify-between text-sm">
                    <span className="text-green-600">Promo (PUNEET20)</span>
                    <span className="text-green-600 font-medium">-₹20.00</span>
                  </div>
                )}
                {codCharges > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">COD Charges</span>
                    <span className="text-navy font-medium">₹{codCharges.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">GST (18%)</span>
                  <span className="text-navy font-medium">₹{gst.toFixed(2)}</span>
                </div>
                <div className="border-t border-gray-200 pt-3 flex justify-between">
                  <span className="font-bold text-navy text-base">Total</span>
                  <span className="font-bold text-navy text-xl" data-testid="total-amount">₹{total.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Pay Button */}
            <div className="sticky bottom-0 bg-white border-t border-gray-100 px-5 py-4">
              <button
                onClick={handlePay}
                disabled={payLoading || !isFormValid}
                className="w-full bg-[#2563EB] hover:bg-[#1d4ed8] text-white font-bold py-4 rounded-full text-base transition-colors shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="pay-btn"
              >
                {payLoading ? "Processing..." : "Place Your Order"}
              </button>
              <p className="text-center text-gray-400 text-xs mt-2">{formData.paymentMethod === "cod" ? "You will pay at the time of delivery" : "Secured by Razorpay"}</p>
            </div>
          </div>
        ) : step === 3 ? (
          /* ========== STEP 3: ORDER CONFIRMED (ALL METHODS) ========== */
          <div className="flex flex-col h-full" data-testid="order-confirmed">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-4 flex items-center justify-between z-10">
              <h2 className="font-heading text-xl font-bold text-navy">Order Confirmed</h2>
              <button onClick={onClose} className="text-gray-400 hover:text-navy p-1" data-testid="close-confirmed"><X size={24} /></button>
            </div>
            <div className="flex-1 px-6 py-8 overflow-y-auto text-center">
              <div className="text-6xl mb-5">😊</div>
              <h2 className="text-2xl md:text-3xl font-bold text-navy mb-2" style={{ fontFamily: "Fraunces, serif" }}>Your order has been placed!</h2>
              <p className="text-gray-500 text-sm mb-6">Thank you, <span className="font-semibold text-navy">{formData.name}</span>, for choosing ETHIA.</p>

              {/* Order details card */}
              <div className="bg-gray-50 rounded-2xl p-5 text-left mb-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Order ID</span>
                  <span className="text-sm font-bold text-navy">#{orderId?.slice(0, 8)}</span>
                </div>
                <div className="border-t border-gray-200 pt-3 space-y-2">
                  {cartItems.map(p => (
                    <div key={p.id} className="flex justify-between text-sm">
                      <span className="text-gray-600">{p.name} x{cart[p.id]}</span>
                      <span className="font-medium text-navy">₹{p.price * cart[p.id]}</span>
                    </div>
                  ))}
                </div>
                <div className="border-t border-gray-200 mt-3 pt-3 flex justify-between">
                  <span className="font-bold text-sm text-navy">Total</span>
                  <span className="font-bold text-lg text-navy">₹{total.toFixed(2)}</span>
                </div>
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <span className="text-xs text-gray-400 uppercase tracking-wider font-bold">Payment</span>
                  <p className="text-sm text-navy mt-1">
                    {formData.paymentMethod === "cod" ? "Cash on Delivery — you will pay at the time of delivery" : "Paid Online — payment received successfully"}
                  </p>
                </div>
              </div>

              {/* Confirmation notices */}
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-left mb-5">
                <div className="flex items-start gap-3 mb-3">
                  <Check size={18} className="text-green-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-green-800">A confirmation email has been sent to <span className="font-semibold">{formData.email}</span></p>
                </div>
                <div className="flex items-start gap-3">
                  <Check size={18} className="text-green-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-green-800">You will receive tracking details on WhatsApp once your order is shipped.</p>
                </div>
              </div>

              {/* Shipping */}
              <div className="bg-gray-50 rounded-xl p-4 text-left mb-6">
                <span className="text-xs text-gray-400 uppercase tracking-wider font-bold">Shipping To</span>
                <p className="text-sm text-navy mt-1">{formData.name}</p>
                <p className="text-xs text-gray-500 mt-0.5">{formData.address}{formData.landmark ? `, ${formData.landmark}` : ""}</p>
                <p className="text-xs text-gray-500">{formData.city}, {formData.state} - {formData.pincode}</p>
                <p className="text-xs text-gray-400 mt-2">Estimated delivery: 5-10 business days</p>
              </div>

              <button onClick={onClose} className="bg-[#7B1F2E] text-white font-bold px-10 py-3.5 rounded-full text-sm hover:opacity-90 transition-opacity" data-testid="confirmed-done-btn">Continue Shopping</button>
              <p className="text-xs text-gray-400 mt-4">For queries: admin@ethia.in | +91 93279 27782</p>
            </div>
          </div>
        ) : null}
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </>
  );
};

export default CheckoutDrawer;
